import { css } from '@emotion/react';
import * as Sentry from '@sentry/react';
import { useBatchedQuery } from 'bank-common-client';
import { notEmpty } from 'folio-common-utils';
import { colors } from 'folio-design-tokens';
import * as React from 'react';
import { LedgerCategoriesDocument } from '../../shared/queries.generated';
import { AnimatedCategorySelector } from './AnimatedCategorySelector';
import { SuggestionSelector } from './SuggestionSelector';
import { CategoryAutosuggest } from './category-autosuggest/CategoryAutosuggest';
import type { Arrow, MainCategory, Subcategory } from './types';

type Props = {
  loading: boolean;
  selectedSubcategoryFid: string | null;
  onSubcategorySelected: (subcategory: Subcategory) => void; // TODO: Support only selecting main category?
  suggestionFids: readonly string[];
};

export const CategorySuggestionSelector: React.FC<Props> = props => {
  const [showingFullSelector, setShowingFullSelector] = React.useState(
    props.suggestionFids.length === 0,
  );
  const { data, error, loading } = useBatchedQuery(LedgerCategoriesDocument);
  const { onSubcategorySelected, selectedSubcategoryFid } = props;
  const [focusEle, setFocusEle] = React.useState<'autosuggest' | 'button'>(
    'autosuggest',
  );

  if (loading) {
    return null;
  }

  if (error) {
    Sentry.captureException(error);
    return (
      <span
        css={css`
          color: red;
        `}
      >
        Fikk en feil under lasting av kategorier
      </span>
    );
  }

  if (data === undefined || data.ledgerInfo === undefined) {
    return <>fant ikke data</>;
  }

  const { groups } = data.ledgerInfo;

  const categories = groups.map<MainCategory & { arrow: Arrow }>(category => ({
    ...category,
    arrow: 'Right',
  }));

  const subcategoriesByName = categories.reduce<Map<string, Subcategory>>(
    (all, mainCategory) => {
      mainCategory.items.forEach(subcategory => {
        all.set(subcategory.title, subcategory);
      });

      return all;
    },
    new Map(),
  );

  const subcategories = Array.from(subcategoriesByName.entries())
    .map<Subcategory>(([_name, subcategory]) => subcategory)
    .filter(e => !e.hidden);

  const selectedFid = selectedSubcategoryFid || props.suggestionFids[0];

  let suggestionFids = Array.from(props.suggestionFids);

  if (selectedFid) {
    suggestionFids = suggestionFids.filter(e => e !== selectedFid);

    suggestionFids.unshift(selectedFid);
  }

  const suggestions = suggestionFids
    .map(suggestionFid => subcategories.find(e => e.fid === suggestionFid))
    .filter(notEmpty)
    .slice(0, 3);

  const selectedSubcategory = subcategories.find(c => c.fid === selectedFid);

  return (
    <>
      <CategoryAutosuggest
        subcategories={subcategories}
        onSubcategorySelected={onSubcategorySelected}
        shouldFocus={focusEle === 'autosuggest'}
        css={css`
          margin-bottom: 0;
        `}
      />
      <div
        css={css`
          margin-top: 16px;
        `}
      >
        {showingFullSelector ? (
          <div
            css={css`
              border: 1px solid ${colors.grayMedium};
              border-radius: 8px;
              margin-top: 8px;
              overflow: hidden;
              /* Fix for Safari issue: https://gist.github.com/ayamflow/b602ab436ac9f05660d9c15190f4fd7b */
              -webkit-mask-image: -webkit-radial-gradient(white, black);

              @media (prefers-contrast: more) {
                border-color: ${colors.black};
              }
            `}
          >
            <AnimatedCategorySelector
              css={css`
                height: 500px;
                overflow-y: scroll;
                -webkit-overflow-scrolling: touch;
              `}
              categories={categories}
              onSubcategorySelected={onSubcategorySelected}
              showBackOption={props.suggestionFids.length > 0}
              onBack={() => {
                setFocusEle('button');
                setShowingFullSelector(false);
              }}
            />
          </div>
        ) : (
          <SuggestionSelector
            suggestions={suggestions}
            selectedSubcategory={selectedSubcategory}
            onSubcategorySelected={onSubcategorySelected}
            onOtherSelected={() => setShowingFullSelector(true)}
            shouldFocus={focusEle === 'button'}
          />
        )}
      </div>
    </>
  );
};
